/**
 * License Service
 * Manages license validation and expiry checks for enterprise deployments
 */

import environment from "../../settings/environmentLoader";

export interface LicenseStatus {
    isValid: boolean;
    isExpired: boolean;
    isExpiringSoon: boolean;
    expiryDate: Date | null;
    daysUntilExpiry: number | null;
    warningMessage: string | null;
}

export class LicenseService {
    /**
     * Check the current license status
     */
    public static getLicenseStatus(): LicenseStatus {
        const licenseConfig = environment.license;
        
        // If no expiry date is configured, license is always valid
        if (!licenseConfig?.expiryDate) {
            return {
                isValid: true,
                isExpired: false,
                isExpiringSoon: false,
                expiryDate: null,
                daysUntilExpiry: null,
                warningMessage: null
            };
        }

        const expiryDate = new Date(licenseConfig.expiryDate);
        const currentDate = new Date();
        const warningDays = licenseConfig.warningDays || 7;
        
        // Check if expired
        const isExpired = currentDate > expiryDate;
        
        // Calculate days until expiry
        const timeDiff = expiryDate.getTime() - currentDate.getTime();
        const daysUntilExpiry = Math.ceil(timeDiff / (1000 * 3600 * 24));
        
        // Check if expiring soon
        const isExpiringSoon = !isExpired && daysUntilExpiry <= warningDays;
        
        // Generate warning message
        let warningMessage: string | null = null;
        if (isExpired) {
            warningMessage = `Your license expired on ${expiryDate.toLocaleDateString()}. Please contact support to renew your license.`;
        } else if (isExpiringSoon) {
            warningMessage = `Your license will expire in ${daysUntilExpiry} day${daysUntilExpiry !== 1 ? 's' : ''} on ${expiryDate.toLocaleDateString()}. Please contact support to renew your license.`;
        }

        return {
            isValid: !isExpired,
            isExpired,
            isExpiringSoon,
            expiryDate,
            daysUntilExpiry: isExpired ? 0 : daysUntilExpiry,
            warningMessage
        };
    }

    /**
     * Check if the application should be blocked due to license expiry
     */
    public static shouldBlockAccess(): boolean {
        const status = this.getLicenseStatus();
        return status.isExpired;
    }

    /**
     * Get formatted expiry date string
     */
    public static getFormattedExpiryDate(): string | null {
        const status = this.getLicenseStatus();
        if (!status.expiryDate) return null;
        
        return status.expiryDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    /**
     * Check if we should show a warning notification
     */
    public static shouldShowWarning(): boolean {
        const status = this.getLicenseStatus();
        return status.isExpiringSoon || status.isExpired;
    }

    /**
     * Get the warning message to display
     */
    public static getWarningMessage(): string | null {
        const status = this.getLicenseStatus();
        return status.warningMessage;
    }
}